// eslint-disable-next-line no-unused-vars
import { DirectedRouteApi, Location } from '@/generated-clients/myTrips'
import { LoadState } from '@util/types'
import { createMapboxAPIClient } from '@/api/MapboxAPIClientFactory'
import MapboxConfig from '@/config/MapboxConfig'
import { createDirectedRouteAPIClient } from '@/api/MyTripsAPIClientFactory'

const { accessToken } = MapboxConfig
const mapboxApiClient = createMapboxAPIClient()
const directedRouteApiClient = createDirectedRouteAPIClient()

function stringifyLocations (locations: [Location]) {
  return locations
    .map(({ latitude, longitude }) => `${longitude},${latitude}`)
    .join(';')
}

function stringifyLocation (location) {
  return `${location.lon},${location.lat}`
}

function bboxFromLocation (location) {
  return `${location.lon - 10},${location.lat - 10},${location.lon + 10},${location.lat + 10}`
}

function createState () {
  return {
    autocompletePlacesRequest: {
      status: LoadState.None,
      response: []
    },
    directionRequest: {
      status: LoadState.None,
      response: {}
    },
    directedRoutesRequest: {
      status: LoadState.None,
      response: []
    },
    searchplacenamesRequest: {
      status: LoadState.None,
      response: []
    }
  }
}

export default {
  namespaced: true,
  state: createState,
  mutations: {
    AUTOCOMPLETE_PLACES_REQUEST (state) {
      state.autocompletePlacesRequest.status = LoadState.Loading
    },
    AUTOCOMPLETE_PLACES_RESPONSE (state, payload) {
      state.autocompletePlacesRequest.status = LoadState.Success
      state.autocompletePlacesRequest.response = payload
    },
    AUTOCOMPLETE_PLACES_ERROR_RESPONSE (state, payload) {
      state.autocompletePlacesRequest.status = LoadState.Error
      state.autocompletePlacesRequest.response = payload
    },
    SEARCH_PLACE_NAMES_REQUEST (state) {
      state.searchplacenamesRequest.status = LoadState.Loading
    },
    SEARCH_PLACE_NAMES_RESPONSE (state, payload) {
      state.searchplacenamesRequest.status = LoadState.Success
      state.searchplacenamesRequest.response = payload
    },
    SEARCH_PLACE_NAMES_ERROR_RESPONSE (state, payload) {
      state.searchplacenamesRequest.status = LoadState.Error
      state.searchplacenamesRequest.response = payload
    },
    DIRECTION_REQUEST (state) {
      state.directionRequest.status = LoadState.Loading
    },
    DIRECTION_RESPONSE (state, payload) {
      state.directionRequest.status = LoadState.Success
      state.directionRequest.response = payload
    },
    DIRECTION_ERROR_RESPONSE (state, payload) {
      state.directionRequest.status = LoadState.Error
      state.directionRequest.response = payload
    },
    DIRECTED_ROUTES_REQUEST (state) {
      state.directedRoutesRequest.status = LoadState.Loading
    },
    DIRECTED_ROUTES_RESPONSE (state, payload) {
      state.directedRoutesRequest.status = LoadState.Success
      state.directedRoutesRequest.response = payload
    },
    DIRECTED_ROUTES_ERROR_RESPONSE (state, payload) {
      state.directedRoutesRequest.status = LoadState.Error
      state.directedRoutesRequest.response = payload
    }
  },
  actions: {
    fetchAutocompletePlaces ({ commit }, { search, lastLocation, poi = false }) {
      commit('AUTOCOMPLETE_PLACES_REQUEST')
      lastLocation = lastLocation || { lon: 16.4106094, lat: 48.189399 }
      return new Promise((resolve, reject) => {
        mapboxApiClient
          .autoComplete(search, accessToken, true, 'de,en', stringifyLocation(lastLocation), bboxFromLocation(lastLocation), 5, 'address' + (poi ? ',poi' : ''))
          .then((response) => {
            commit('AUTOCOMPLETE_PLACES_RESPONSE', response.data)
            resolve(response.data)
          })
          .catch((error) => {
            commit('AUTOCOMPLETE_PLACES_ERROR_RESPONSE', error.response)
            reject(error)
          })
      })
    },

    fetchPlaceNames ({ commit }, { longitude, latitude, poi = false }) {
      commit('SEARCH_PLACE_NAMES_REQUEST')
      return new Promise((resolve, reject) => {
        mapboxApiClient
          .searchPlaceName([longitude, latitude], accessToken, 'de,en', 1, 'address' + (poi ? ',poi' : ''))
          .then((response) => {
            commit('SEARCH_PLACE_NAMES_RESPONSE', response.data)
            resolve(response.data)
          })
          .catch((error) => {
            commit('SEARCH_PLACE_NAMES_ERROR_RESPONSE', error.response)
            reject(error)
          })
      })
    },

    fetchDirection ({ commit }, { locations }) {
      const coordinates = stringifyLocations(locations)

      commit('DIRECTION_REQUEST')
      return new Promise((resolve, reject) => {
        mapboxApiClient
          .getDirection(coordinates, accessToken, false, 'geojson', 'simplified', null)
          .then((response) => {
            commit('DIRECTION_RESPONSE', response.data)
            resolve(response.data)
          })
          .catch((error) => {
            commit('DIRECTION_ERROR_RESPONSE', error.response)
            reject(error)
          })
      })
    },

    fetchDirectedRoutes ({ commit }) {
      commit('DIRECTED_ROUTES_REQUEST')

      return new Promise((resolve, reject) => {
        directedRouteApiClient
          .getDirectedRoutes()
          .then((response) => {
            commit('DIRECTED_ROUTES_RESPONSE', response.data)
            resolve(response.data)
          })
          .catch((error) => {
            commit('DIRECTED_ROUTES_ERROR_RESPONSE', error.response)
            reject(error)
          })
      })
    }

  },
  getters: {
    getAutocompletePlacesStatus: state => state.autocompletePlacesRequest.status,
    getAutocompletePlaces: state => state.autocompletePlacesRequest.response,
    getMostRelevantPlaceProvince: state => {
      const places = state.autocompletePlacesRequest.response
      const features = places?.features || []
      if (features.length === 0) {
        return null
      }
      const mostAccurateResult = features.reduce((curr, next) => next.relevance > curr.relevance ? next : curr)
      const regionContext = mostAccurateResult?.context.find(ctx => ctx.id.includes('region'))
      const placeContext = mostAccurateResult?.context.find(ctx => ctx.id.includes('place'))
      const provinceContext = regionContext || placeContext
      return provinceContext?.text_de || provinceContext?.text || null
    },
    getPlaceNamesStatus: state => state.searchplacenamesRequest.status,
    getPlaceNamesResponse: state => state.searchplacenamesRequest.response,
    getDirectionStatus: state => state.directionRequest.status,
    getDirectionResponse: state => state.directionRequest.response,
    getDirectedRoutesStatus: state => state.directedRoutesRequest.status,
    getDirectedRoutesResponse: state => state.directedRoutesRequest.response
  },
}

